import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';

/**
 * Write list state into the dashboard's URL, the same way the project and contract
 * tables do: trim the value, drop it from the URL when it is empty or the default, and
 * keep the remaining params sorted so the string is stable.
 *
 * Any change that alters *what* the list contains resets the page — otherwise a search
 * run from page four asks the server for page four of a much shorter result set and the
 * grid comes back empty.
 */
export const useApplyFilters = () => {
  const router = useRouter();
  const searchParams = useSearchParams();

  return (updates: Record<string, string>, { resetPage = true } = {}) => {
    const newSearchParams = new URLSearchParams(searchParams);
    let changed = false;

    for (const [name, value] of Object.entries(updates)) {
      const cleanedValue = value.trim();

      if (searchParams.get(name) !== cleanedValue) {
        changed = true;
      }

      if (cleanedValue) {
        newSearchParams.set(name, cleanedValue);
      } else {
        newSearchParams.delete(name);
      }
    }

    if (resetPage && changed) {
      newSearchParams.delete('page');
    }

    newSearchParams.sort();

    router.push(
      {
        pathname: '/',
        query: newSearchParams.toString(),
      },
      undefined,
      {
        shallow: true,
        scroll: false,
      },
    );
  };
};
